import { useMemo, useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from "@/components/ui/dialog";
import PageHeader from "@/components/common/PageHeader";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import EmptyState from "@/components/common/EmptyState";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  Bold,
  Italic,
  Link as LinkIcon,
  Image as ImageIcon,
  List,
  Code,
  Heading2,
  Quote,
  Eye,
  Calendar,
  Globe,
  FileText,
  Upload,
  Loader2,
  Heading,
  AlignLeft,
  AlignJustify,
  Image,
  Tag,
  Monitor
} from "lucide-react";
import api from "@/lib/api";
import { BACKEND_URL } from "@/config";

const blank = {
  title: "",
  slug: "",
  summary: "",
  content: "",
  featuredImageUrl: "",
  isPublished: false,
  publishedDate: new Date().toISOString().slice(0, 10),
  tags: "",
  seoTitle: "",
  seoDescription: ""
};

const toolbarBtns = [
  { icon: Bold, label: "Bold" },
  { icon: Italic, label: "Italic" },
  { icon: Heading2, label: "Heading" },
  { icon: List, label: "List" },
  { icon: LinkIcon, label: "Link" },
  { icon: ImageIcon, label: "Image" },
  { icon: Quote, label: "Quote" },
  { icon: Code, label: "Code" }
];

export default function Blogs() {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [modalOpen, setModalOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewing, setPreviewing] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [imageName, setImageName] = useState("No file selected");

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const res = await api.get("/Blog");
      if (res.success) {
        setItems(res.data || []);
      } else {
        toast.error(res.message || "Failed to fetch blogs");
      }
    } catch (err) {
      toast.error(err?.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  const filtered = useMemo(() => {
    let arr = items;
    if (filter === "published") arr = arr.filter((b) => b.isPublished);
    if (filter === "draft") arr = arr.filter((b) => !b.isPublished);
    if (query) {
      const q = query.toLowerCase();
      arr = arr.filter(
        (b) => b.title?.toLowerCase().includes(q) || b.summary?.toLowerCase().includes(q)
      );
    }
    return arr;
  }, [items, query, filter]);

  const stats = {
    total: items.length,
    published: items.filter((b) => b.isPublished).length,
    drafts: items.filter((b) => !b.isPublished).length
  };

  const slugify = (s) =>
    s ? s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") : "";

  const openCreate = () => {
    setEditing(null);
    setForm(blank);
    setImageName("No file selected");
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      ...item,
      publishedDate: item.publishedDate ? item.publishedDate.substring(0, 10) : ""
    });
    setImageName(item.featuredImageUrl ? item.featuredImageUrl.split('/').pop() : "No file selected");
    setModalOpen(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageName(file.name);
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const res = await api.post('/Blog/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.success) {
        setForm({ ...form, featuredImageUrl: res.data });
        toast.success("Image uploaded");
      } else {
        toast.error(res.message || "Upload failed");
      }
    } catch (err) {
      toast.error(err.message || "Failed to upload image");
    }
  };

  const save = async () => {
    if (!form.title) {
      toast.error("Title is required.");
      return;
    }
    const payload = {
      ...form,
      slug: form.slug || slugify(form.title),
      publishedDate: form.publishedDate ? `${form.publishedDate}T00:00:00Z` : null
    };

    try {
      if (editing) {
        const res = await api.put(`/Blog/${editing.id}`, payload);
        if (res.success) {
          toast.success(payload.isPublished ? "Blog updated and published." : "Blog draft saved.");
          fetchBlogs();
          setModalOpen(false);
        } else {
          toast.error(res.message || "Update failed");
        }
      } else {
        const res = await api.post("/Blog", payload);
        if (res.success) {
          toast.success(payload.isPublished ? "Blog published." : "Draft created.");
          fetchBlogs();
          setModalOpen(false);
        } else {
          toast.error(res.message || "Creation failed");
        }
      }
    } catch (err) {
      toast.error(err?.message || "An error occurred");
    }
  };

  const handleDelete = async () => {
    try {
      const res = await api.delete(`/Blog/${toDelete.id}`);
      if (res.success) {
        toast.success("Blog post deleted.");
        fetchBlogs();
      } else {
        toast.error(res.message || "Failed to delete");
      }
    } catch (err) {
      toast.error(err?.message || "An error occurred");
    } finally {
      setConfirmOpen(false);
    }
  };

  const getMediaUrl = (path) => {
    const backendUrl = BACKEND_URL;
    return path ? (path.startsWith('http') ? path : `${backendUrl}${path}`) : "";
  };

  return (
    <div>
      <PageHeader
        title="Blogs"
        description="Write, edit and publish essays from your portfolio's CMS."
        testId="blogs"
        action={openCreate}
        actionLabel="Create Blog"
        actionIcon={Plus}
      />

      {/* Stats strip */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <StatPill label="Total posts" value={stats.total} testid="blog-stat-total" />
        <StatPill label="Published" value={stats.published} testid="blog-stat-published" />
        <StatPill label="Drafts" value={stats.drafts} testid="blog-stat-drafts" />
      </div>

      <Card className="p-4 mb-5">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
            <Input
              data-testid="blog-search"
              placeholder="Search blog posts…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9 h-9"
            />
          </div>
          <Tabs value={filter} onValueChange={setFilter} data-testid="blog-filter">
            <TabsList>
              <TabsTrigger value="all" data-testid="blog-filter-all">All</TabsTrigger>
              <TabsTrigger value="published" data-testid="blog-filter-published">Published</TabsTrigger>
              <TabsTrigger value="draft" data-testid="blog-filter-draft">Drafts</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </Card>

      {loading ? (
        <div className="flex h-[400px] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No blog posts"
          description="Start writing your first essay. Drafts stay private until you publish."
          actionLabel="Create Blog"
          onAction={openCreate}
          testId="blogs-empty"
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5">
          {filtered.map((b) => (
            <Card key={b.id} data-testid={`blog-card-${b.id}`} className="group p-0 overflow-hidden hover:border-foreground/20 transition-colors">
              <div className="relative h-40 bg-muted overflow-hidden flex items-center justify-center">
                {b.featuredImageUrl ? (
                  <img src={getMediaUrl(b.featuredImageUrl)} alt={b.title} className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <FileText className="h-10 w-10 text-muted-foreground/30" />
                )}
                <Badge
                  className={`absolute top-3 left-3 border-0 backdrop-blur text-[10px] font-mono ${b.isPublished ? "bg-primary/90 text-primary-foreground" : "bg-background/90 text-foreground"}`}
                >
                  {b.isPublished ? "Published" : "Draft"}
                </Badge>
              </div>
              <div className="p-5">
                <h3 className="font-display text-base font-semibold tracking-tight line-clamp-2 min-h-[2.75rem]">{b.title}</h3>
                <p className="text-[11px] font-mono text-muted-foreground mt-1.5">/{b.slug}</p>
                <p className="text-sm text-muted-foreground mt-3 leading-relaxed line-clamp-3">{b.summary}</p>
                <div className="flex items-center justify-between gap-2 mt-4 pt-4 border-t border-border">
                  <span className="text-[11px] font-mono text-muted-foreground inline-flex items-center gap-1">
                    <Calendar className="h-3 w-3" strokeWidth={1.5} />
                    {b.publishedDate ? b.publishedDate.substring(0, 10) : ""}
                  </span>
                  <div className="flex items-center gap-0.5">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setPreviewing(b); setPreviewOpen(true); }} data-testid={`blog-preview-${b.id}`}>
                      <Eye className="h-3 w-3" strokeWidth={1.5} />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(b)} data-testid={`blog-edit-${b.id}`}>
                      <Pencil className="h-3 w-3" strokeWidth={1.5} />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-destructive"
                      onClick={() => { setToDelete(b); setConfirmOpen(true); }}
                      data-testid={`blog-delete-${b.id}`}
                    >
                      <Trash2 className="h-3 w-3" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Editor modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto scrollbar-thin" data-testid="blog-modal">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit blog post" : "New blog post"}</DialogTitle>
            <DialogDescription>Write, format and publish. Drafts stay private until you flip the switch.</DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="content">
            <TabsList className="mb-4">
              <TabsTrigger value="content" data-testid="blog-tab-content">Content</TabsTrigger>
              <TabsTrigger value="seo" data-testid="blog-tab-seo">SEO & meta</TabsTrigger>
            </TabsList>

            <TabsContent value="content" className="space-y-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <Heading className="h-3.5 w-3.5" strokeWidth={2} />
                  Title
                </Label>
                <Input
                  data-testid="blog-title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value, slug: form.slug || slugify(e.target.value) })}
                  placeholder="An essay title"
                  className="mt-1.5 text-lg font-display"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <LinkIcon className="h-3.5 w-3.5" strokeWidth={2} />
                  Slug
                </Label>
                <Input
                  data-testid="blog-slug"
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })}
                  className="mt-1.5 font-mono text-sm"
                  placeholder="my-essay-title"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <AlignLeft className="h-3.5 w-3.5" strokeWidth={2} />
                  Summary
                </Label>
                <Textarea
                  data-testid="blog-summary"
                  value={form.summary}
                  onChange={(e) => setForm({ ...form, summary: e.target.value })}
                  rows={2}
                  className="mt-1.5"
                />
              </div>

              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <AlignJustify className="h-3.5 w-3.5" strokeWidth={2} />
                  Content
                </Label>
                <div className="mt-1.5 border border-border rounded-md overflow-hidden focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background">
                  <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-border bg-muted/30">
                    {toolbarBtns.map((b, i) => {
                      const Icon = b.icon;
                      return (
                        <button
                          key={b.label}
                          type="button"
                          title={b.label}
                          data-testid={`editor-${b.label.toLowerCase()}`}
                          className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-background transition-colors"
                        >
                          <Icon className="h-3.5 w-3.5" strokeWidth={1.5} />
                        </button>
                      );
                    })}
                  </div>
                  <Textarea
                    data-testid="blog-content"
                    value={form.content}
                    onChange={(e) => setForm({ ...form, content: e.target.value })}
                    rows={10}
                    className="border-0 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0 resize-none font-mono text-sm"
                    placeholder="Start writing your essay…"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <UploadCard
                  label="Featured Image"
                  description="Upload banner for display"
                  fileName={imageName}
                  testid="blog-image"
                  icon={ImageIcon}
                  onChange={handleImageUpload}
                />
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                      <Calendar className="h-3.5 w-3.5" strokeWidth={2} />
                      Publish Date
                    </Label>
                    <Input
                      type="date"
                      data-testid="blog-date"
                      value={form.publishedDate}
                      onChange={(e) => setForm({ ...form, publishedDate: e.target.value })}
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                      <Tag className="h-3.5 w-3.5" strokeWidth={2} />
                      Tags
                    </Label>
                    <Input
                      data-testid="blog-tags"
                      value={form.tags}
                      onChange={(e) => setForm({ ...form, tags: e.target.value })}
                      className="mt-1.5"
                      placeholder="React, Frontend, Web"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between rounded-md border border-border p-3">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Monitor className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Publish Status</Label>
                    <p className="text-xs text-muted-foreground">Make this post visible on the portfolio.</p>
                  </div>
                </div>
                <Switch
                  checked={form.isPublished}
                  onCheckedChange={(v) => setForm({ ...form, isPublished: v })}
                  data-testid="blog-publish-toggle"
                />
              </div>
            </TabsContent>

            <TabsContent value="seo" className="space-y-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <Globe className="h-3.5 w-3.5" strokeWidth={2} />
                  SEO Title
                </Label>
                <Input
                  data-testid="blog-seo-title"
                  value={form.seoTitle}
                  onChange={(e) => setForm({ ...form, seoTitle: e.target.value })}
                  className="mt-1.5"
                  placeholder="Recommended ≤ 60 chars"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <FileText className="h-3.5 w-3.5" strokeWidth={2} />
                  SEO Description
                </Label>
                <Textarea
                  data-testid="blog-seo-description"
                  value={form.seoDescription}
                  onChange={(e) => setForm({ ...form, seoDescription: e.target.value })}
                  rows={3}
                  className="mt-1.5"
                  placeholder="Recommended ≤ 160 chars"
                />
              </div>
              <div className="rounded-md border border-border p-4 bg-muted/30">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Search preview</p>
                <p className="text-[11px] text-muted-foreground font-mono truncate">folioadmin.io › blogs › {form.slug}</p>
                <p className="text-base text-primary mt-0.5 line-clamp-1">{form.seoTitle || form.title || "Title goes here"}</p>
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{form.seoDescription || form.summary || "Description goes here"}</p>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="blog-save">{form.isPublished ? "Save & publish" : "Save draft"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview modal */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto scrollbar-thin" data-testid="blog-preview-modal">
          {previewing && (
            <>
              <div className="h-44 -m-6 mb-0 overflow-hidden bg-muted flex items-center justify-center">
                {previewing.featuredImageUrl ? (
                  <img src={getMediaUrl(previewing.featuredImageUrl)} alt={previewing.title} className="w-full h-full object-cover" />
                ) : (
                  <FileText className="h-10 w-10 text-muted-foreground/30" />
                )}
              </div>
              <div className="pt-6">
                <Badge variant="secondary" className="text-[10px] font-mono">{previewing.isPublished ? "Published" : "Draft"}</Badge>
                <h2 className="font-display text-2xl font-bold tracking-tight mt-3">{previewing.title}</h2>
                <p className="text-xs font-mono text-muted-foreground mt-1.5">
                  <Globe className="inline h-3 w-3 mr-1" strokeWidth={1.5} />
                  /blogs/{previewing.slug} · {previewing.publishedDate ? previewing.publishedDate.substring(0, 10) : ""}
                </p>
                <p className="text-sm text-muted-foreground mt-4 italic leading-relaxed">{previewing.summary}</p>
                <div
                  className="text-sm mt-5 leading-relaxed prose prose-sm dark:prose-invert max-w-none"
                  dangerouslySetInnerHTML={{ __html: previewing.content }}
                />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Delete blog post?"
        description="This will permanently remove the post from your portfolio."
        confirmLabel="Delete"
        onConfirm={handleDelete}
      />
    </div>
  );
}

const StatPill = ({ label, value, testid }) => (
  <Card data-testid={testid} className="p-4">
    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className="font-display text-2xl font-bold mt-1">{value}</p>
  </Card>
);

const UploadCard = ({ label, description, fileName, testid, icon: Icon, onChange }) => (
  <div>
    <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
      <Icon className="h-3.5 w-3.5" strokeWidth={2} />
      {label}
    </Label>
    <label className="mt-1.5 block cursor-pointer group">
      <div className="flex items-center gap-3 border-2 border-dashed border-border/60 bg-muted/30 rounded-xl px-4 py-4 hover:border-primary/50 hover:bg-primary/5 transition-all duration-300">
        <span className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
          <Icon className="h-5 w-5" strokeWidth={1.5} />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold truncate group-hover:text-primary transition-colors">{fileName !== "No file selected" ? fileName : "Click to upload"}</p>
          <p className="text-[11px] font-mono text-muted-foreground truncate">{description}</p>
        </div>
        <Upload className="h-4 w-4 text-primary shrink-0 opacity-50 group-hover:opacity-100 transition-opacity" strokeWidth={2} />
      </div>
      <input type="file" className="hidden" data-testid={testid} onChange={onChange} />
    </label>
  </div>
);
